// PowerReviews feedless render module for the Shopify storefront.
//
// The core snippet (snippets/powerreviews.liquid) loads PWR's ui.js, defines the
// window.pwr queue, and emits a hidden #pr-config carrying the merchant
// credentials from theme settings. Each PDP emits PWR containers
// (#pr-reviewsnippet under the title, #pr-reviewdisplay below the fold) carrying
// the per-product identity sourced from the pim.pwr_* metafields as data
// attributes; the store-owned Write-a-Review page emits a single #pr-write. This
// module reads that DOM and makes a single pwr("render", …) call.
//
// It also post-processes what PWR renders back into the review display: see
// DERIVED TITLES at the foot of this file.
//
// We follow PWR's documented Shopify Manual flow with one delta: page_id /
// page_id_variant come from our rollup metafields (pim.pwr_page_id /
// pim.pwr_page_id_variant), not the default product.id / variant.id — so reviews
// aggregate at the product_line + color/motif level. See
// docs/powerreviews-integration-plan.md and docs/adr/adr-20260608-01-powerreviews-integration-model.md.

// Reads the merchant credentials the core snippet emitted into #pr-config.
export function readCredentials(doc) {
    const cfg = doc.getElementById('pr-config');
    if (!cfg) return {};
    return {
        api_key: cfg.dataset.apiKey,
        merchant_group_id: cfg.dataset.merchantGroupId,
        merchant_id: cfg.dataset.merchantId,
        locale: cfg.dataset.locale,
    };
}

// Make the rendered snippet's review-count link scroll the review list into view
// (replaces the legacy #shopify-product-reviews anchor). PWR renders the count as
// an <a class="pr-snippet-review-count"> inside #pr-reviewsnippet; this delegates
// from the container so it works regardless of when PWR hydrates.
export function wireSnippetScroll(doc) {
    const snippet = doc.getElementById('pr-reviewsnippet');
    const display = doc.getElementById('pr-reviewdisplay');
    if (!snippet || !display) return;

    snippet.addEventListener('click', (event) => {
        if (event.target.closest('.pr-snippet-review-count')) {
            event.preventDefault();
            display.scrollIntoView({ behavior: 'smooth' });
        }
    });
}

// The Enhanced-SEO `subject` object PWR's render config takes: the very same
// schema.org/Product the theme publishes as JSON-LD, handed over verbatim.
//
// Since Phase 1A (issue #550) the theme builds that object once, in
// theme/snippets/product-schema.liquid, and serves it two ways — as the page's
// `<script type="application/ld+json">`, and as the `data-pr-subject` attribute
// on the PDP container this reads. One object, two carriers, so PWR's copy and
// the published copy cannot drift. Before 1A there were eight
// `data-pr-subject-*` attributes reassembled here into a near-copy of what
// microdata-schema.liquid emitted separately; that was two descriptions of one
// product maintained in two files.
//
// Inert in production today — ENABLE_CLIENT_SIDE_STRUCTURED_DATA is false on
// every PDP element, so PWR generates no schema to merge it into. Sent anyway:
// it is what a rollback of that key restores.
//
// Returns undefined when the container carries no subject (samples, Bath and
// metafield-less products emit no PWR container at all; PLP cards use a
// different component) or when the attribute is not parseable JSON — a subject
// is an enrichment, and failing to build one must never take the render call
// down with it.
export function buildSubject(dataset) {
    if (!dataset.prSubject) return undefined;
    try {
        return JSON.parse(dataset.prSubject);
    } catch {
        return undefined;
    }
}

// Preview-theme-only override (pwr_test_page_id theme setting). Blank on the
// published theme. When set, every PDP renders this page_id instead of the
// product's rollup metafield, so the widgets can be verified against a known
// page_id that already has reviews before the legacy→rollup migration runs.
//
// "Preview theme" rather than "staging theme": theme settings live in
// config/settings_data.json, which is Admin-owned and excluded from both push
// and pull (adr-20260520-02), so this can be set independently on any
// unpublished theme — including a duplicate of the live production theme, which
// is what the Phase 1A probe used (docs/geo/pwr-structured-data-probe.md).
export function readTestPageId(doc) {
    const cfg = doc.getElementById('pr-config');
    return (cfg && cfg.dataset.testPageId) || '';
}

// Build the PDP element from whichever review containers are on the page
// (#pr-reviewsnippet under the title, #pr-reviewdisplay below the fold). Returns
// null when neither is present — sample/Bath/missing-metafield PDPs, and non-PDP
// pages such as the Write-a-Review page.
export function buildPdpElement(doc) {
    const snippet = doc.getElementById('pr-reviewsnippet');
    const display = doc.getElementById('pr-reviewdisplay');

    // Identity comes from whichever container exists (both carry the same
    // metafield-sourced data attributes) so a display-only page still resolves a
    // page_id.
    const identitySource = snippet || display;
    if (!identitySource) return null;

    const credentials = readCredentials(doc);
    const testPageId = readTestPageId(doc);
    const { prPageId, prPageIdVariant, prVariantName, prReviewWrapperUrl } = identitySource.dataset;

    const components = {};
    if (snippet) components.ReviewSnippet = 'pr-reviewsnippet';
    if (display) components.ReviewDisplay = 'pr-reviewdisplay';

    const element = {
        ...credentials,
        page_id: testPageId || prPageId,
        review_wrapper_url: prReviewWrapperUrl,
        // Render the review list as one continuously-scrolling column
        // ("load more") instead of PWR's default numbered pagination. Only
        // affects the ReviewDisplay component; a no-op on snippet-only PDPs.
        REVIEW_DISPLAY_PAGINATION_TYPE: 'VERTICAL',
        components,
    };

    // Legacy QS page_ids carry no page_id_variant, so the test override shows all
    // of that page_id's reviews unfiltered. Otherwise attach our single rollup
    // variant (color/motif).
    if (!testPageId) {
        element.product = {
            variants: [{ page_id_variant: prPageIdVariant, name: prVariantName }],
        };
    }

    // The theme is the sole emitter of PDP `Product` JSON-LD (issue #550,
    // theme/snippets/product-schema.liquid), so PWR must not add one of its own:
    // two `@type: Product` on a page is the defect adr-20260901-01 exists to
    // prevent. A per-element render-config key documented in PWR's Shopify
    // Manual, so this is ours to set rather than a portal setting.
    //
    // Unconditional, and deliberately so. It shipped first as a probe behind
    // `pdp_pwr_schema_probe_enabled`; on 2026-09-10 a fetch of a real PDP on an
    // unpublished duplicate of the live theme confirmed PWR emits no `Product`
    // with it set and renders its review UI, histogram, sort, search and Write
    // button unchanged (docs/geo/pwr-structured-data-probe.md, issue #546). The
    // flag came out with Phase 1A — a probe left wired to a theme setting is how
    // a preview affordance becomes production behavior by accident.
    //
    // Rolling this back means restoring the subject-fed Product PWR used to
    // emit AND removing the theme's own, in that order, or the page carries two.
    element.ENABLE_CLIENT_SIDE_STRUCTURED_DATA = false;

    // Enhanced SEO: the schema.org/Product subject. Inert while
    // ENABLE_CLIENT_SIDE_STRUCTURED_DATA is false — PWR generates no schema to
    // merge it into — and still sent for two reasons. It is the rollback:
    // flipping that key back on has to restore the exact configuration the probe
    // measured, not a stripped one. And since Phase 1A it is no longer a second
    // description of the product at all: the theme hands PWR the *same object*
    // it publishes as JSON-LD (theme/snippets/product-schema.liquid, read off
    // the container's data-pr-subject attribute), so the two cannot disagree.
    //
    // Independent of the test-page-id override — it describes this product
    // regardless of which page_id's reviews are displayed.
    const subject = buildSubject(identitySource.dataset);
    if (subject) {
        element.subject = subject;
    }

    // When both the snippet and the list are present, make the snippet's review
    // count scroll to the list once PWR has rendered it.
    if (snippet && display) {
        element.on_render = () => wireSnippetScroll(doc);
    }

    return element;
}

// The three hidden MSQs (issue #289) — prefilled from the pr_<key> URL params and
// removed from the visible form by CSS (.pr-<key>-form-group). We surface their
// values back to the reviewer as a read-only summary so they can see what product
// they're reviewing. Order is size → finish → fulfillment; the label is ours (the
// MSQ's own question text is a long prompt, not a caption). Keyed by the PWR field
// input id (pr-<key>-input) confirmed against the live WAR form.
const WAR_MSQS = [
    { id: 'pr-size-input', label: 'Size' },
    { id: 'pr-finish-input', label: 'Finish' },
    { id: 'pr-fulfillmentpath-input', label: 'Fulfillment' },
];

// Inject the read-only MSQ summary into the WAR form once PWR has rendered it.
// Reads each hidden MSQ input's prefilled value and emits a <dl> row per MSQ that
// actually carries a value — so an unprefilled WAR visit (no pr_* params) shows
// nothing. Idempotent (guards on #pr-msq-summary) so it's safe to re-run on each
// PWR render, e.g. after a validation re-render.
export function renderWriteMsqSummary(doc) {
    const form = doc.getElementById('pr-war-form');
    if (!form || doc.getElementById('pr-msq-summary')) return;

    const rows = WAR_MSQS.map(({ id, label }) => ({
        label,
        value: (doc.getElementById(id)?.value || '').trim(),
    })).filter((row) => row.value);
    if (!rows.length) return;

    const summary = doc.createElement('div');
    summary.id = 'pr-msq-summary';
    summary.className = 'pr-msq-summary';

    const dl = doc.createElement('dl');
    for (const { label, value } of rows) {
        const dt = doc.createElement('dt');
        dt.textContent = label;
        const dd = doc.createElement('dd');
        dd.textContent = value;
        dl.append(dt, dd);
    }
    summary.appendChild(dl);
    form.prepend(summary);
}

// Build the Write-a-Review element for the store-owned WAR page
// (/pages/write-a-review), whose Admin body is a single <div id="pr-write">.
// Returns null on every other page. PWR's ui.js reads page_id and all pr_*
// prefill params (pr_page_id, pr_page_id_variant, pr_source, order_id, and the
// MSQ pr_<key> params) straight from the URL query string, so the render config
// carries only the merchant credentials and the component mapping — no page_id.
// See docs/powerreviews-integration-plan.md (Review-request email / WAR) and PWR's
// Shopify Manual, Step 5.
export function buildWriteElement(doc) {
    if (!doc.getElementById('pr-write')) return null;

    return {
        ...readCredentials(doc),
        // Surface the hidden, prefilled MSQs as a read-only summary once the form
        // has rendered.
        on_render: () => renderWriteMsqSummary(doc),
        // Scroll back to the top of the form on submit so the reviewer lands on the
        // thank-you state (adopted from PWR's reference WAR config).
        on_submit: () => window.scrollTo(0, 0),
        components: { Write: 'pr-write' },
    };
}

// One render call per page. The PDP element and the Write element are
// independent — a normal PDP has only the former, the WAR page only the latter —
// so we build both and drop whichever isn't present.
export function buildPwrElements(doc) {
    return [buildPdpElement(doc), buildWriteElement(doc)].filter(Boolean);
}

// Detect on-page PWR containers and make a single pwr("render", …) call.
export function loadPowerReviews(pwr, doc) {
    const elements = buildPwrElements(doc);
    if (elements.length) {
        pwr('render', elements);
    }
}

// ---------------------------------------------------------------------------
// DERIVED TITLES (issue #438)
// ---------------------------------------------------------------------------
//
// A large share of the Reviews on PDPs arrived through an NPS import whose PWR
// importer synthesized a title by cutting the first 60 characters of the review
// body, with no regard for word boundaries — a *Derived Title* (CONTEXT.md). A
// shopper sees the same words twice, the first copy chopped mid-word:
//
//   Kia was very efficient, she provided all the information nee
//   Kia was very efficient, she provided all the information needed and also…
//
// Where a title is detectably just a machine-truncated copy of the opening of its
// body, we present it as an intentional excerpt instead: trim back to the last
// whole word and end with an ellipsis. Reviewer-written titles are untouched.
//
// Display-only in the sense that PWR's stored data is never changed — the
// pseudo-titles stay in PWR's portal, review emails, and any export. The same
// treatment is applied to the JSON-LD PWR injects, so the structured titles agree
// with the visible ones: see DERIVED TITLES IN STRUCTURED DATA below (issue #439).
// PWR exposes no source/import metadata on a rendered Review, so detection has to
// be heuristic; the thresholds below were measured against the live rendered DOM.

// The whole selector contract with PowerReviews, verified against the live
// rendered DOM. Both title surfaces sit inside #pr-reviewdisplay, so one root
// covers both, but they do *not* share class names:
//
//   - the review list emits <h2 class="pr-rd-review-headline"> + a
//     <p class="pr-rd-description-text"> body, both inside a .pr-review card;
//   - the snapshot faceoff ("Most Liked Positive/Negative Review") emits
//     <h2 class="pr-faceoff-title"> + a <p class="pr-faceoff-review"> body as
//     siblings under .pr-faceoff-positive / .pr-faceoff-negative.
//
// The faceoff body is clamped as leading text + a literal "..." + a display:none
// span holding the remainder, so its full text is in the DOM. That injected
// ellipsis lands far past the ~60 characters a Derived Title spans, so it cannot
// affect the prefix comparison.
//
// Both title elements carry no child markup, so replacing their text content loses
// nothing. The list headline's id is referenced by the per-Review action buttons'
// aria-describedby, so rewriting the text keeps what a screen reader announces in
// agreement with what is on screen automatically.
//
// Undocumented PWR internals: if any of these is renamed the transform matches
// nothing on that surface and the page renders PWR's original titles. No offline
// test can catch a vendor-side rename, so the DOM suite pins all four names as
// literals in its fixtures instead — editing a selector here without updating the
// fixture fails CI, which makes the contract explicit at the one moment someone is
// looking at it.
const PWR_REVIEW_SURFACES = [
    { title: '.pr-rd-review-headline', body: '.pr-rd-description-text' },
    { title: '.pr-faceoff-title', body: '.pr-faceoff-review' },
];

// Observed Derived Titles measured 44–60 characters. The floor sits below that
// population with margin and guards the false positive that matters: a reviewer
// writing a genuinely short title that happens to open their body ("Beautiful
// tile") must not be given an ellipsis implying text that was never cut.
const DERIVED_TITLE_MIN_LENGTH = 40;

// Compare title and body with runs of whitespace collapsed — a real Review on the
// page has a title reading "…visit your store in San Fra" against a body with a
// double space after "your", which a raw prefix comparison misses.
function normalizeReviewText(s) {
    return String(s).replace(/\s+/g, ' ').trim();
}

// The Review body paired with a title element on the given surface. Walks up from
// the title rather than keying off PWR's card class names, which differ between the
// two surfaces (a list card wraps both in .pr-review; a faceoff card makes them
// siblings). Two stops keep the pairing honest, since mismatching a title against
// someone else's body would be worse than leaving the title alone: bail as soon as
// the subtree holds a second title (a card with no body of its own can never borrow
// its neighbour's), and never walk past the review display container or the
// caller's root.
function findReviewBody(headline, surface, root) {
    for (let el = headline.parentElement; el; el = el.parentElement) {
        if (el.querySelectorAll(surface.title).length > 1) return null;
        const body = el.querySelector(surface.body);
        if (body) return body;
        if (el === root || el.id === 'pr-reviewdisplay') return null;
    }
    return null;
}

// Both arguments normalized. A title is a Derived Title when all four hold: it
// clears the length floor, the body is strictly longer (a truncation actually
// occurred), the body starts with the title, and the title does not already end on
// a sentence boundary. That last condition is the carve-out for imported titles
// that happened to land on a sentence break and already read as complete thoughts
// ("Working with Brittany was thoughtful and seamless."), which would otherwise
// become "seamless.…".
function isDerivedTitle(title, body) {
    return (
        title.length >= DERIVED_TITLE_MIN_LENGTH &&
        body.length > title.length &&
        body.startsWith(title) &&
        !/[.!?]$/.test(title)
    );
}

// Both arguments normalized, and isDerivedTitle already true. The body tells us
// whether the cut landed on a word boundary, so the final word is kept or dropped
// on evidence rather than guessed. Output ends in an ellipsis (U+2026, not three
// periods), which is also what makes the transform idempotent: on a second pass
// the rewritten title is no longer a prefix of the body.
function formatDerivedTitle(title, body) {
    // The character the body continues with decides whether the title's last word
    // survived the cut whole. Whitespace is the obvious boundary, but punctuation is
    // one too — a cut landing on the comma of "...service was great, but the lead
    // time" left "great" intact, so trimming it would throw away a good word. The
    // exceptions are the marks that live *inside* a word: a cut at the hyphen of
    // "hand-glazed" or the apostrophe of "don't" really did split one.
    const lastWordComplete = /[^\w'’-]/.test(body.charAt(title.length));
    const withoutFragment = title.replace(/\s*\S+$/, '');
    // A title with no whitespace at all — a body opening with a pasted URL, or a
    // script that does not space-delimit words — has no earlier boundary to trim
    // back to, and dropping its only word would leave a bare ellipsis. Keep it
    // whole in that case: mid-word beats empty.
    const base = lastWordComplete || !withoutFragment ? title : withoutFragment;
    return `${base.replace(/[,;:]+$/, '')}…`;
}

// Rewrite every Derived Title within `root` — a container element or a document —
// on both title surfaces. Bodies are never touched, only titles, so review search
// highlighting in the body survives.
export function applyDerivedTitles(root) {
    // A document has no parentElement chain to stop at, so the walk bounds itself
    // on the review display container instead (see findReviewBody).
    const rootElement = root.nodeType === 1 ? root : null;

    for (const surface of PWR_REVIEW_SURFACES) {
        for (const headline of root.querySelectorAll(surface.title)) {
            const bodyElement = findReviewBody(headline, surface, rootElement);
            if (!bodyElement) continue;

            // Text content, not markup: an active review search wraps matched terms
            // in emphasis inside the body, which would otherwise defeat the
            // comparison.
            const title = normalizeReviewText(headline.textContent);
            const body = normalizeReviewText(bodyElement.textContent);
            if (!isDerivedTitle(title, body)) continue;

            headline.textContent = formatDerivedTitle(title, body);
        }
    }
}

// Keep the transform applied for the lifetime of the page via a MutationObserver
// scoped to the review display container, and return the observer (null on pages
// with no container — WAR, samples, Bath, missing metafield).
//
// An observer rather than PWR's on_render callback: that callback is not guaranteed
// to re-fire for "Show More Reviews", for any of the six sort options, for review
// search, or for rating filters, and each of those re-renders the list. A list
// whose first page is fixed but which reverts as the shopper loads or re-sorts is
// worse than no fix at all.
//
// Wired at init rather than in buildPdpElement because the two are independent:
// the builder attaches its render callback only when both PDP containers are
// present, whereas this needs to run wherever the review list renders. (It also
// keeps this clear of the Enhanced SEO work, which edits that same function.)
export function observeDerivedTitles(doc) {
    const display = doc.getElementById('pr-reviewdisplay');
    if (!display || typeof MutationObserver === 'undefined') return null;

    const observer = new MutationObserver(() => {
        applyDerivedTitles(display);
        // Reentrancy guard: discard the records our own title writes just queued so
        // the observer is never driven by its own output. (The transform is also
        // idempotent by construction, so this is belt and braces.)
        observer.takeRecords();
    });
    observer.observe(display, { childList: true, subtree: true, characterData: true });

    // PWR normally hydrates well after init, but apply once up front so an already
    // rendered list (e.g. a re-attach) is covered too.
    applyDerivedTitles(display);
    return observer;
}

// ---------------------------------------------------------------------------
// DERIVED TITLES IN STRUCTURED DATA (issue #439)
// ---------------------------------------------------------------------------
//
// PWR emits its own schema.org/Product JSON-LD, and each entry in its `review`
// array carries the raw Derived Title as `name`, paired with an intact
// `reviewBody`. Left alone, the truncated titles a shopper no longer sees remain in
// the structured data.
//
// Why this is reachable from theme code at all — the question #439 asks first. The
// `subject` object above is the only SEO input PWR's Shopify integration accepts,
// and it carries Product-level fields only (name, description, offers); PWR's
// Shopify SEO doc (docs/powerreviews/shopify-seo.pdf) exposes no config surface for
// `review[]`, which PWR generates from its own review records. So the render config
// is not the lever. But PWR has no server-side role in a Shopify page render — its
// whole integration is ui.js running client-side — so the JSON-LD is *injected into
// the DOM*, which puts it on exactly the same post-processing seam the display fix
// uses. Reachable in the DOM, not in the config.
//
// One thing is easier here than on the display surfaces: a Review object carries
// its own body, so the title/body pairing is exact and needs no DOM walk. The
// detection and formatting rules are shared with the display transform verbatim, so
// the two surfaces cannot drift.
//
// Location-agnostic by design: we do not know, and do not want to depend on,
// whether PWR appends its script to <head> or beside the widget, so every JSON-LD
// script on the page is considered and a script is rewritten only when it actually
// holds a Derived Title. Scripts with no reviews (BreadcrumbList, Organization, the
// showroom schemas) are never even re-serialized.
const JSON_LD_SELECTOR = 'script[type="application/ld+json"]';

// Rewrite the Derived Titles in the value of a `review` property — an array of
// Reviews, or a single unwrapped Review. True when anything changed.
//
// A Review is identified by carrying both a string `name` and a string
// `reviewBody`, not by `@type: "Review"`: sitting under `review` already
// establishes what it is, so requiring a vendor-written type string as well would
// only add a way for PWR to switch the transform off by renaming or omitting it.
function rewriteReviews(review) {
    if (Array.isArray(review)) {
        return review.reduce((changed, item) => rewriteReviews(item) || changed, false);
    }
    if (!review || typeof review !== 'object') return false;
    if (typeof review.name !== 'string' || typeof review.reviewBody !== 'string') return false;

    const name = normalizeReviewText(review.name);
    const body = normalizeReviewText(review.reviewBody);
    if (!isDerivedTitle(name, body)) return false;

    review.name = formatDerivedTitle(name, body);
    return true;
}

// Walk a parsed JSON-LD value and rewrite every Derived Title within it, in place.
// Returns true when anything changed.
//
// The walk recurses so a Product nested under an `@graph` wrapper is reached, but
// only ever rewrites what sits under a `review` property — the exact scope #439
// asks for. Matching on the property path rather than on "any object anywhere
// carrying a name and a reviewBody" keeps the transform off schema that isn't a
// product's reviews, whoever emitted it.
function rewriteJsonLdReviewNames(node) {
    if (Array.isArray(node)) {
        return node.reduce((changed, item) => rewriteJsonLdReviewNames(item) || changed, false);
    }
    if (!node || typeof node !== 'object') return false;

    let changed = false;
    for (const [key, value] of Object.entries(node)) {
        if (!value || typeof value !== 'object') continue;
        changed = rewriteJsonLdReviewNames(value) || changed;
        if (key === 'review') changed = rewriteReviews(value) || changed;
    }
    return changed;
}

// Apply the Derived Title treatment to every JSON-LD script within `root` (a
// container element or a document). Fails safe: a script whose contents are not
// JSON is left exactly as it is, and a script holding no Derived Title is not
// rewritten at all, so unrelated schema is preserved byte for byte.
export function applyJsonLdDerivedTitles(root) {
    for (const script of root.querySelectorAll(JSON_LD_SELECTOR)) {
        let parsed;
        try {
            parsed = JSON.parse(script.textContent);
        } catch {
            continue;
        }
        if (!rewriteJsonLdReviewNames(parsed)) continue;
        script.textContent = JSON.stringify(parsed);
    }
}

// ---------------------------------------------------------------------------
// CLIENT-SIDE aggregateRating (issue #550)
// ---------------------------------------------------------------------------
//
// The theme server-renders a complete Product + Offer on every priced PDP
// (theme/snippets/product-schema.liquid) and deliberately puts no rating in it:
// PowerReviews' rollup is line-level — 4.78 / 412 across all of Natural Press —
// so stamping it on a single 3x6 as server-rendered fact is a claim we would
// have to defend to every crawler. adr-20260901-01 records that the
// server-rendered Product carries neither aggregateRating nor review[], and
// theme/tests/schema-contract.test.js asserts both as positives.
//
// Google is the exception the same ADR carves out, and this is its one
// permitted shape: the page is already complete and correct without this, and
// it runs only where JavaScript does. Google's merchant-listing stars are
// generated from an aggregateRating, and suppressing PWR's Product removed the
// only one on the page — so without this, Phase 1A would have traded the star
// snippets for nothing.
//
// PULLING IT. Set the constant below to false and deploy. That is the whole
// procedure: nothing else reads it, the page stays valid, and the only thing
// that changes is that Google stops seeing a rating. Pull it the day GSC shows
// a structured-data problem attributable to the rating — a Merchant listings
// error on ratingValue/reviewCount, or a manual action. The pin in
// theme/tests/powerreviews.dom.test.js fails when you flip it, which is
// deliberate: it puts whoever flips it in front of this comment and
// adr-20260730-01's amendment rather than in front of a green suite.
export const CLIENT_SIDE_AGGREGATE_RATING = true;

// What PWR leaves in the rendered DOM, and the whole read contract with it.
// These two class names are undocumented vendor internals, exactly like the
// Derived Title selectors above: if PWR renames either, this finds nothing and
// the page keeps its (complete, correct) server-rendered Product with no
// rating. The DOM suite pins both as literals in its fixtures so a rename here
// without a fixture edit fails CI.
const RATING_DECIMAL_SELECTOR = '.pr-snippet-rating-decimal';
const REVIEW_COUNT_SELECTOR = '.pr-snippet-review-count';

// The rating PWR rendered, or null when there isn't a usable one.
//
// Rounded to one decimal, and that is the ceiling: once PWR's own Product is
// suppressed its precise 4.78 is exposed nowhere — not in the DOM, and not on
// the window.POWERREVIEWS global, which carries only `display` (measured
// 2026-09-10, docs/geo/pwr-structured-data-probe.md). 4.8 and 4.78 render
// identical stars, so the cost is cosmetic, but it is a real difference from
// what PWR used to publish.
export function readRenderedRating(doc) {
    const decimal = doc.querySelector(RATING_DECIMAL_SELECTOR);
    const count = doc.querySelector(REVIEW_COUNT_SELECTOR);
    if (!decimal || !count) return null;

    const ratingValue = Number.parseFloat(decimal.textContent);
    // "412 Reviews", and "1,412 Reviews" once a line gets there.
    const reviewCount = Number.parseInt(count.textContent.replace(/[^\d]/g, ''), 10);

    // A product with no reviews yet renders a 0 count. Google requires a
    // positive ratingCount, and "rated 0 by 0 people" is worse than no rating at
    // all. The scale bound catches a vendor-side format change (a percentage, a
    // "4.8 out of 5" in the same node) rather than publishing a nonsense rating.
    if (!Number.isFinite(ratingValue) || ratingValue <= 0 || ratingValue > 5) return null;
    if (!Number.isInteger(reviewCount) || reviewCount <= 0) return null;

    return { ratingValue, reviewCount };
}

// Add the rendered rating to the Product the theme server-rendered. Returns true
// when a script was actually rewritten.
//
// Only a `Product` is ever touched, and only one: a PDP has exactly one, and the
// pages that legitimately have none (the $0 trim archetype) or carry a
// `ProductGroup` instead (Combined Listing parents) are left exactly as served.
// The publisher graph and the BreadcrumbList are never re-serialized.
export function applyAggregateRating(doc) {
    const rating = readRenderedRating(doc);
    if (!rating) return false;

    const aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: rating.ratingValue,
        reviewCount: rating.reviewCount,
    };

    for (const script of doc.querySelectorAll(JSON_LD_SELECTOR)) {
        let parsed;
        try {
            parsed = JSON.parse(script.textContent);
        } catch {
            continue;
        }

        // Top-level or inside an @graph — the same two shapes extractTypes walks
        // in scripts/geo-schema-assertions.js.
        const nodes = Array.isArray(parsed['@graph']) ? parsed['@graph'] : [parsed];
        const product = nodes.find((node) => node && node['@type'] === 'Product');
        if (!product) continue;

        // Re-running on each PWR re-render is the normal case (sort, filter,
        // "show more" all re-render the snippet), and the rating rarely moves.
        // Writing only on a real change keeps this off the observer's own back.
        const current = product.aggregateRating;
        if (
            current &&
            current.ratingValue === aggregateRating.ratingValue &&
            current.reviewCount === aggregateRating.reviewCount
        ) {
            return false;
        }

        product.aggregateRating = aggregateRating;
        script.textContent = JSON.stringify(parsed);
        return true;
    }

    return false;
}

// Keep the rating applied for the lifetime of the page, and return the observer
// (null on a page with no PWR container, or with the switch pulled).
//
// An observer because PWR hydrates well after init and re-renders as the shopper
// sorts, filters and searches. Scoped to a PWR container rather than the
// document: that is where the values render, and it keeps this off the rest of
// the page's churn. The JSON-LD it writes to lives outside that subtree, so its
// own writes cannot re-trigger it.
//
// Either container qualifies, as in buildPdpElement: the buy-box snippet is
// where the rating normally renders, but a display-only template (the specs
// page, which has no buy-box snippet block) still gets a Review Snapshot. The
// read itself is document-wide, so whichever container PWR renders into is
// found — this only decides what to watch.
export function observeAggregateRating(doc) {
    if (!CLIENT_SIDE_AGGREGATE_RATING) return null;

    const container =
        doc.getElementById('pr-reviewsnippet') || doc.getElementById('pr-reviewdisplay');
    if (!container || typeof MutationObserver === 'undefined') return null;

    const observer = new MutationObserver(() => applyAggregateRating(doc));
    observer.observe(container, { childList: true, subtree: true, characterData: true });

    // Cover a rating PWR has already rendered (e.g. a re-attach).
    applyAggregateRating(doc);
    return observer;
}

// Auto-init on the storefront: the core snippet defines window.pwr (the ui.js
// queue) before loading this module, then we render once the DOM is ready. The
// length guard in loadPowerReviews makes this a no-op on pages with no PWR
// container (samples, Bath, missing metafield), so importing this module under a
// test runner with an empty document does nothing.
if (typeof window !== 'undefined') {
    const init = () => {
        loadPowerReviews(window.pwr, document);
        // Both independent of the render call, and both watching what PWR
        // hydrates client-side: the first cleans up Derived Titles on the visible
        // review surfaces, the second copies the rendered rating into the
        // Product the theme server-rendered.
        observeDerivedTitles(document);
        observeAggregateRating(document);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}
